import React, { useEffect, useState } from "react";
import FloatingInput from "@/commons/components/fields/floatingInput/FloatingInput";
import CustomSelect from "@/commons/components/fields/customSelect/CustomSelect";

/**
 * FormBuilder sequenziale a più livelli
 */
export default function FormBuilder({ formElement, onChange, onSubmitDetected }) {
    const [values, setValues] = useState({});

    useEffect(() => {
        if (onChange) {
            onChange(values);
        }
    }, [values, onChange]);

    function handleBlockChange(blockData) {
        setValues((prev) => ({ ...prev, ...blockData }));
    }

    if (!formElement) return null;

    if (Array.isArray(formElement)) {
        return (
            <>
                {formElement.map((elem, idx) => (
                    <Block
                        key={idx}
                        element={elem}
                        onBlockChange={handleBlockChange}
                        onSubmitDetected={onSubmitDetected}
                    />
                ))}
            </>
        );
    }

    return (
        <Block
            element={formElement}
            onBlockChange={handleBlockChange}
            onSubmitDetected={onSubmitDetected}
        />
    );
}

function Block({ element, onBlockChange, onSubmitDetected }) {
    const {
        type,
        name,
        label,
        defaultValue = "",
        formScope,
        childrens = []
    } = element || {};

    const isNoInputType = ["submit", "option", "message", "default"].includes(
        (type || "").toLowerCase()
    );

    const [open, setOpen] = useState(isNoInputType ? true : false);
    const [localValue, setLocalValue] = useState(defaultValue);
    const [coords, setCoords] = useState({ x: "", y: "", z: "" });
    const [comboChecked, setComboChecked] = useState({});

    useEffect(() => {
        if ((type || "").toLowerCase() === "submit" && onSubmitDetected) {
            onSubmitDetected(name);
        }
    }, [type, name, onSubmitDetected]);

    function notify(fieldName, val) {
        onBlockChange({ [fieldName]: val });
    }

    function isValid(value, blockType) {
        switch (blockType) {
            case "text":
            case "selection":
                return value.trim() !== "";
            case "number":
                return value !== "";
            case "coordinates":
                return value.x !== "" && value.y !== "" && value.z !== "";
            case "combo":
                return Object.values(value).some(checked => checked === true);
            default:
                return true;
        }
    }

    let mainContent = null;

    switch ((type || "").toLowerCase()) {
        case "text":
            mainContent = (
                <form-box>
                    <form-content>
                        <FloatingInput
                            type="text"
                            label={label}
                            name={name}
                            defaultValue={defaultValue}
                            onChange={(e) => {
                                const val = e.target.value;
                                setLocalValue(val);
                                notify(label, val);
                                setOpen(isValid(val, "text"));
                            }}
                        />
                    </form-content>
                    <br />
                </form-box>
            );
            break;

        case "number":
            mainContent = (
                <form-box>
                    <br />
                    <form-content>
                        <FloatingInput
                            label={label}
                            type="number"
                            name={name}
                            defaultValue={defaultValue}
                            onChange={(e) => {
                                const val = e.target.value;
                                setLocalValue(val);
                                notify(label, val);
                                setOpen(isValid(val, "number"));
                            }}
                        />
                    </form-content>
                    <br />
                </form-box>
            );
            break;

        case "coordinates":
        function handleCoordChange(coordName, val) {
            setCoords(prev => {
                const updated = { ...prev, [coordName]: val };
                const coordsStr = `${updated.x} ${updated.y} ${updated.z}`;
                notify(label, coordsStr);
                setOpen(isValid(updated, "coordinates"));
                return updated;
            });
        }
            mainContent = (
                <form-box>
                    <br />
                    <form-content name="coords" class="coords-input">
                        <h1>{label}</h1>
                        <coords-container>
                            <FloatingInput
                                label={"X"}
                                type="number"
                                onChange={(e) => handleCoordChange("x", e.target.value)}
                            />
                            <FloatingInput
                                label={"Y"}
                                type="number"
                                onChange={(e) => handleCoordChange("y", e.target.value)}
                            />
                            <FloatingInput
                                label={"Z"}
                                type="number"
                                onChange={(e) => handleCoordChange("z", e.target.value)}
                            />
                        </coords-container>
                        <br />
                    </form-content>
                    <br />
                </form-box>
            );
            break;

        case "selection":
            const options = childrens
                .filter(opt => (opt.type || "").toLowerCase() === "option")
                .map(opt => ({
                    label: opt.label,
                    value: opt.name
                }));
            mainContent = (
                <form-box>
                    <form-content>
                        <CustomSelect
                            label={label}
                            options={options}
                            value={localValue}
                            defaultValue={label}
                            onChange={(val) => {
                                setLocalValue(val);
                                notify(label, val);
                                setOpen(isValid(val, "selection"));
                            }}
                        />
                    </form-content>
                    <br />
                </form-box>
            );
            break;

        case "combo":
            mainContent = (
                <form-box>
                    <label id={name}>{label}</label>
                    <br />
                    <form-content name="combo">
                        {childrens.map((opt, idx) => {
                            if ((opt.type || "").toLowerCase() === "option") {
                                return (
                                    <comboBox key={idx}>
                                        <FloatingInput
                                            type="checkbox"
                                            onChange={(e) => {
                                                const updated = { ...comboChecked, [opt.name]: e.target.checked };
                                                let str = "";
                                                Object.entries(updated).forEach(([k, v]) => {
                                                    str += `${k}=${v ? "Yes" : "No"}; `;
                                                });
                                                notify(label, str);

                                                const isVal = Object.values(updated).some(ch => ch);
                                                setOpen(isVal);
                                                setComboChecked(updated);
                                            }}
                                        />
                                        <label>
                                            <span className="vis-checkbox" />
                                            {opt.label}
                                        </label>
                                    </comboBox>
                                );
                            }
                            return (
                                <Block
                                    key={idx}
                                    element={opt}
                                    onBlockChange={onBlockChange}
                                    onSubmitDetected={onSubmitDetected}
                                />
                            );
                        })}
                    </form-content>
                    <br />
                </form-box>
            );
            break;

        case "option":
        case "submit":
            break;

        case "message":
            mainContent = (
                <div>
                    <br />
                    <span dangerouslySetInnerHTML={{ __html: label }} />
                    <br />
                </div>
            );
            break;

        default:
            mainContent = (
                <div>
                    <b>default</b> {label}
                </div>
            );
            break;
    }

    let childBlocks = null;
    if (open) {
        childBlocks = childrens.map((child, idx) => {
            const scope = (child.formScope || "").toLowerCase();
            if (scope !== "parameter") {
                return (
                    <div key={idx}>
                        <Block
                            element={child}
                            onBlockChange={onBlockChange}
                            onSubmitDetected={onSubmitDetected}
                        />
                    </div>
                );
            } else {
                if (localValue === child.name) {
                    return (
                        <div key={idx}>
                            <Block
                                element={child}
                                onBlockChange={onBlockChange}
                                onSubmitDetected={onSubmitDetected}
                            />
                        </div>
                    );
                }
                return null;
            }
        });
    }

    return (
        <div>
            {mainContent}
            {childBlocks}
        </div>
    );
}
